import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPatch, ApiError } from "../lib/api.js";
import { Button } from "../components/Button.js";

/**
 * Rename a form, and set the line of description that sits under its title.
 *
 * `PATCH /forms/:id` has accepted both since Phase 3, but nothing ever sent them: a form's title
 * could be chosen once at creation and never again, and its description could not be set at all,
 * from anywhere. A typo in a form's name was permanent.
 *
 * An inline strip under the row, matching the duplicate and delete controls beside it, rather than
 * a modal — the list of names is the context you are renaming against.
 *
 * **The public URL is deliberately not editable here.** `/f/{slug}` is printed on posters, sits in
 * QR codes and is inside every invitation already sent; changing it silently breaks all of them.
 * The server agrees — `formUpdateRequestSchema` has no `slug` — so this is the one field the strip
 * shows read-only rather than simply omitting, because "can I change the address too?" is the
 * first question a rename raises.
 */
export const RenameFormStrip = ({
  form,
  onCancel,
  onRenamed,
}: {
  form: { id: string; title: string; slug: string; description?: string | null; status: string };
  onCancel: () => void;
  onRenamed: () => void;
}): JSX.Element => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(form.title);
  const [description, setDescription] = useState(form.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => titleRef.current?.select(), []);

  const rename = useMutation({
    mutationFn: () =>
      apiPatch(`/forms/${form.id}`, {
        title: title.trim(),
        // "" clears it. The API takes null for that, and an empty string would otherwise be stored
        // as a description that renders as an empty paragraph on the form.
        description: description.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["forms"] });
      onRenamed();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "This form could not be renamed."),
  });

  const unchanged =
    title.trim() === form.title && (description.trim() || null) === (form.description ?? null);

  return (
    <form
      className="space-y-2 border-t bg-muted/40 px-4 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        if (title.trim()) rename.mutate();
      }}
    >
      <p className="text-xs text-muted-foreground">
        {form.status === "PUBLISHED"
          ? "Applicants see the new title the moment you save it, including anyone part-way through the form. Its web address does not change, so existing links, posters and QR codes keep working."
          : "The web address does not change, so any link you have already shared keeps working."}
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[16rem] flex-1">
          <span className="mb-1 block text-[11px] uppercase text-muted-foreground">Title</span>
          <input
            ref={titleRef}
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={300}
            required
          />
        </label>

        <label className="min-w-[16rem] flex-[2]">
          <span className="mb-1 block text-[11px] uppercase text-muted-foreground">
            Description (optional)
          </span>
          <input
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={2000}
            placeholder="One line, shown under the title on the form"
          />
        </label>

        <label className="min-w-[12rem] flex-1">
          <span className="mb-1 block text-[11px] uppercase text-muted-foreground">
            Public URL (cannot change)
          </span>
          <span className="flex items-center rounded-md border bg-muted pl-2 text-sm text-muted-foreground">
            <span>/f/</span>
            <input
              className="w-full bg-transparent px-1 py-1.5 text-sm outline-none"
              value={form.slug}
              readOnly
              tabIndex={-1}
              aria-label="Public URL, which a rename does not change"
            />
          </span>
        </label>

        <Button variant="primary" type="submit" disabled={rename.isPending || unchanged}>
          {rename.isPending ? "Saving…" : "Save"}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </form>
  );
};
